import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useEffect, useRef, useState } from 'react';

import { useGoogleMaps } from '../../../../../contexts/google-map-context/GoogleMapContent';
import { useAppSelector } from '../../../../../redux/hooks';
import device from '../../../../../utils/api/device';

/**
 * check the lat and lng is valid or not
 * @param {any} position - The position
 * @param {any} position.latitude - The latitude prop
 * @param {any} position.longitude - The longitude prop
 * @returns {any} The result
 */
const validatePosition = (position: { latitude: any; longitude: any }) => {
  const lat = parseFloat(position.latitude);
  const lng = parseFloat(position.longitude);
  return !Number.isNaN(lat) && !Number.isNaN(lng) ? { lat, lng } : null;
};

interface DeviceMapProperties {
  height?: string;
  color?: string;
}

/**
 * Generates an SVG station marker icon encoded as a data URI.
 * @param {string} color - The hex color code for the marker.
 * @returns {string} The data URI of the generated SVG icon.
 */
const getStationMarkerIcon = (color: string): string => {
  const svg = `
    <svg width="44" height="45" viewBox="0 0 44 45"
      fill="none" xmlns="http://www.w3.org/2000/svg">

      <path
        d="M22 2.91602C14.4186 2.91602 8.25 8.81563 8.25 16.0645C8.25 19.5166 9.82352 24.1073 12.9267 29.7096C15.4189 34.2076 18.3021 38.275 19.8017 40.2988C20.0551 40.6446 20.3864 40.9259 20.7688 41.1197C21.1512 41.3136 21.5739 41.4146 22.0026 41.4146C22.4313 41.4146 22.854 41.3136 23.2363 41.1197C23.6187 40.9259 23.95 40.6446 24.2034 40.2988C25.7005 38.275 28.5863 34.2076 31.0784 29.7096C34.1765 24.1091 35.75 19.5183 35.75 16.0645C35.75 8.81563 29.5814 2.91602 22 2.91602Z"
        fill="${color}"
      />

      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M23.2551 19.1954H28.9444C29.3221 19.1944 29.6668 19.386 29.8276 19.6883C29.9884 19.9907 29.9367 20.3475 29.6944 20.6041L19.3279 31.6541C19.1341 31.8446 18.8213 31.9 18.5603 31.7892C18.2993 31.6784 18.1517 31.4285 18.2002 31.1757L20.3058 21.7309H15.0504C14.7553 21.7289 14.4766 21.6094 14.2927 21.4052C14.1088 21.202 14.0394 20.9356 14.1033 20.6809L16.9226 9.63769C17.0184 9.24687 17.4083 8.96785 17.8609 8.96591H25.2198C25.5259 8.96202 25.8145 9.08938 25.9951 9.30715C26.1823 9.51812 26.2428 9.79616 26.1591 10.0538L23.2551 19.1954Z"
        fill="white"
      />
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/**
 * Component to display device map
 * @param {any} properties - The properties
 * @returns {any} The result
 */
function DeviceMap(properties: Readonly<DeviceMapProperties>) {
  const { height = '500px', color = '#22c55e' } = properties;
  const { isLoaded } = useGoogleMaps();
  const mapReference = useRef<google.maps.Map | null>(null);
  const { selectedChargingPoint } = useAppSelector((state) => state.device);

  const [chargingPointLocation, setChargingPointLocation] = useState<{
    latitude: string;
    longitude: string;
  }>({
    latitude: '0',
    longitude: '0',
  });

  const containerStyle = {
    width: '100%',
    height,
  };

  /**
   * General details
   * @param {any} id - The id
   */
  const handleFetchDeviceLocation = async (id: string) => {
    try {
      const response = await device.fetchChargingPointLocation(id);
      setChargingPointLocation(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (selectedChargingPoint) {
      handleFetchDeviceLocation(selectedChargingPoint.id);
    }
  }, [selectedChargingPoint]);

  return (
    <div className="device-map-wrap">
      <div className="device-map-toggle">
        {/* <h6></h6>
        <h6></h6> */}
      </div>
      <div className="device-map-container">
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={{
              lat: Number(chargingPointLocation.latitude),
              lng: Number(chargingPointLocation.longitude),
            }}
            zoom={5}
            onLoad={(map) => {
              mapReference.current = map; // Assign the map instance to the reference
            }}>
            {chargingPointLocation &&
              (() => {
                const validPosition = validatePosition(chargingPointLocation);
                return validPosition ? (
                  <Marker
                    key="01"
                    position={validPosition}
                    // clusterer={clusterer}
                    icon={{
                      url: getStationMarkerIcon(color),
                      scaledSize: new window.google.maps.Size(44, 45), // Width, Height
                    }}
                  />
                ) : null;
              })()}
          </GoogleMap>
        ) : null}
      </div>
    </div>
  );
}

export default DeviceMap;
